import * as React from "react"

export type SortDir = "asc" | "desc"

/** Per-column value accessors used by `sortRows`. */
export type SortAccessors<T> = Record<
  string,
  (row: T) => string | number | null | undefined
>

interface SortState {
  key: string | null
  dir: SortDir
}

/**
 * Column-sort state + a stable, numeric-aware sorter.
 *
 * - Numbers compare numerically, strings via localeCompare.
 * - null/undefined/NaN values always sink to the bottom, regardless of direction.
 * - Sort is stable (original order preserved among ties).
 * - Toggling the active column flips direction; a new column starts ascending.
 */
export function useTableSort<T>(defaultKey?: string, defaultDir: SortDir = "asc") {
  const [sort, setSort] = React.useState<SortState>({
    key: defaultKey ?? null,
    dir: defaultDir,
  })

  const toggleSort = React.useCallback((key: string) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    )
  }, [])

  const sortRows = React.useCallback(
    (rows: T[], accessors: SortAccessors<T>): T[] => {
      if (!sort.key) return rows
      const accessor = accessors[sort.key]
      if (!accessor) return rows
      const mult = sort.dir === "asc" ? 1 : -1
      const isMissing = (v: string | number | null | undefined) =>
        v === null || v === undefined || (typeof v === "number" && Number.isNaN(v))
      return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const av = accessor(a.row)
          const bv = accessor(b.row)
          const aMissing = isMissing(av)
          const bMissing = isMissing(bv)
          if (aMissing && bMissing) return a.index - b.index
          if (aMissing) return 1 // missing sinks regardless of direction
          if (bMissing) return -1
          let c: number
          if (typeof av === "number" && typeof bv === "number") c = av - bv
          else c = String(av).localeCompare(String(bv))
          if (c !== 0) return c * mult
          return a.index - b.index // stable tiebreak
        })
        .map((entry) => entry.row)
    },
    [sort.key, sort.dir]
  )

  return { sortKey: sort.key, sortDir: sort.dir, toggleSort, sortRows }
}
